import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Unlock, UserPlus, X, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RefundModal = ({ eventId, team, member, headers, onClose, onDone }) => {
  const [mode, setMode] = useState('cashfree');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  useEffect(() => {
    const q = member?.registration_id ? `&registration_id=${member.registration_id}` : '';
    axios.get(`${API}/api/event-teams/company/${eventId}/refunds/quote?team_id=${team.id}${q}`, { headers })
      .then((r) => { setQuote(r.data); if (!r.data.cashfree_available) setMode('manual'); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load payment'));
  }, []);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { team_id: team.id, registration_id: member?.registration_id || null, mode, note, amount: amount ? parseFloat(amount) : null };
      const r = await axios.post(`${API}/api/event-teams/company/${eventId}/refunds`, body,
        { headers: { ...headers, 'Idempotency-Key': `${team.id}-${member?.id || 'team'}-${amount || 'full'}-${mode}-${Math.floor(Date.now() / 60000)}` } });
      toast.success(r.data.message); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refund failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="refund-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-[#191919]">Refund {member ? member.name : `team ${team.name}`}</h3>
        {quote && (
          <p className="text-sm text-[#191919]" data-testid="refund-quote">Paid <b>INR {quote.paid}</b> · already refunded INR {quote.refunded} · refundable <b>INR {quote.remaining}</b></p>
        )}
        <div className="grid grid-cols-2 gap-2">
          {[['cashfree', 'Refund via Cashfree', 'Money goes back automatically'], ['manual', 'Record manual refund', 'You send the money yourself']]
            .filter(([v]) => v === 'manual' || !quote || quote.cashfree_available).map(([v, t, d]) => (
            <button key={v} onClick={() => setMode(v)} data-testid={`refund-mode-${v}`}
              className={`text-left p-3 rounded-lg border-2 ${mode === v ? 'border-[#0a66c2] bg-[#0a66c2]/5' : 'border-gray-200'}`}>
              <p className="text-sm font-semibold text-[#191919]">{t}</p><p className="text-[11px] text-[#666]">{d}</p>
            </button>
          ))}
        </div>
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={quote ? `Amount in INR (max ${quote.remaining})` : 'Amount in INR'}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#191919]" data-testid="refund-amount-input" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason / note" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-[#191919]" data-testid="refund-note-input" />
        {quote && !quote.cashfree_available && <p className="text-[11px] text-amber-700">This payment didn't go through Cashfree, so only a manual refund can be recorded.</p>}
        <p className="text-[11px] text-[#666]">Leaving the amount blank refunds the full refundable amount. A full refund cancels the affected ticket(s){member ? ' and removes this member' : ' and the team'}.</p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#666]">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="refund-submit-btn">{busy ? 'Processing…' : 'Issue refund'}</button>
        </div>
      </div>
    </div>
  );
};

// Expanded-row organizer controls for one team
export const TeamControls = ({ eventId, team, headers, onChange }) => {
  const [email, setEmail] = useState('');
  const [refundFor, setRefundFor] = useState(null);
  const base = `${API}/api/event-teams/company/${eventId}/teams/${team.id}`;
  const run = async (fn) => {
    try { const r = await fn(); toast.success(r.data.message); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };
  const live = team.status !== 'cancelled';
  return (
    <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 space-y-3" data-testid={`team-controls-${team.id}`}>
      <div className="space-y-1.5">
        {team.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 text-xs bg-white border border-gray-200 rounded-lg px-3 py-2">
            <span className="text-[#191919] font-medium">{m.name} <span className="text-[#666] font-normal">· {m.email} · {m.role}</span></span>
            <span className="flex items-center gap-3 text-[#666]">
              <span className="capitalize">{(m.payment_status || '').replace('_', ' ')}</span>
              <span>ticket {m.ticket_status}</span>
              {m.checked_in && <span className="text-emerald-700 font-medium">checked in</span>}
              {m.payment_status === 'paid' && !team.payment_status?.startsWith('paid') && (
                <button onClick={() => setRefundFor({ member: m })} className="text-red-600 font-medium" data-testid={`refund-member-${m.id}`}>Refund</button>
              )}
              {live && <button onClick={() => window.confirm(`Remove ${m.name} from ${team.name}?`) && run(() => axios.delete(`${base}/members/${m.id}`, { headers }))} title="Remove" data-testid={`org-remove-member-${m.id}`}><X size={14} className="hover:text-red-600" /></button>}
            </span>
          </div>
        ))}
      </div>
      {live && (
        <div className="flex flex-wrap items-center gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Add / swap in member by email" className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs w-64 text-[#191919]" data-testid={`org-add-email-${team.id}`} />
          <button onClick={() => email && run(() => axios.post(`${base}/members`, { email }, { headers })).then(() => setEmail(''))} className="flex items-center gap-1 px-3 py-1.5 bg-[#0a66c2] text-white rounded-lg text-xs font-medium" data-testid={`org-add-btn-${team.id}`}><UserPlus size={13} /> Add</button>
          {team.status === 'locked' && <button onClick={() => run(() => axios.post(`${base}/unlock`, {}, { headers }))} className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-[#191919]" data-testid={`org-unlock-${team.id}`}><Unlock size={13} /> Unlock team</button>}
          {team.payment_status === 'paid' && <button onClick={() => setRefundFor({ member: null })} className="flex items-center gap-1 px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs font-medium ml-auto" data-testid={`refund-team-${team.id}`}><RotateCcw size={13} /> Refund team</button>}
        </div>
      )}
      {refundFor && <RefundModal eventId={eventId} team={team} member={refundFor.member} headers={headers} onClose={() => setRefundFor(null)} onDone={onChange} />}
    </div>
  );
};
